import axios from 'axios';
import { apiClient } from './api/apiClient';
import { ApiItemResponse, unwrapItem } from './api/response';

/** Y: 점유(주차 불가), N: 빈자리 — 현장 router parking_locations.status 그대로. */
export type ParkingQaStatus = 'Y' | 'N';

export interface ParkingQaSpace {
  id: number;
  name: string;
  status: ParkingQaStatus;
  isFixed: boolean;
}
export interface ParkingQaCctv {
  id: number;
  name: string;
  levelName: string | null;
  /** QA 고정 중 — 이 CCTV 의 카메라 값이 DB 에 반영되지 않는다. */
  held: boolean;
  spaces: ParkingQaSpace[];
}
export interface ParkingQaVerdict {
  cctvName: string;
  full: boolean;
  control: 'red' | 'green';
}

/** 통합서버 DB 의 한 층. mapData 는 맵 에디터 내보내기의 mapData 그대로다. */
export interface QaMapLevel {
  id: number;
  lot: string;
  name: string;
  mapData: Record<string, unknown>;
}

/** 현장 DB 의 주차면 하나. level 은 연결 CCTV 의 층 (CCTV 미연결이면 없음). */
export interface QaSpace {
  id: number;
  name: string;
  type: string;
  status: ParkingQaStatus;
  fixed: boolean;
  cctv?: string;
  level?: string;
}
/** 단면 유도 전광판 한 대 — 방향(칸)마다 세는 주차면. */
export interface QaBoard {
  id: number;
  name: string;
  ip: string;
  groups: { direction: string; spaceIds: number[] }[];
}
export interface QaState {
  spaces: QaSpace[];
  boards: QaBoard[];
}
/** CCTV 상세 — ROI 는 디워핑 프레임 픽셀 좌표 [x0,y0,x1,y1,...]. */
export interface QaCctvDetail {
  name: string;
  ip: string;
  server?: string;
  spaces: QaSpace[];
  rois: { parking: string; roi: number[] }[];
  /** 카메라가 마지막으로 낸 판정 (redis cctv:<name>). 없으면 null. */
  detection: { timestamp: number; parking: { id: string; status: boolean }[] } | null;
  holdSeconds: number;
  now: number;
  /** 최근 배경(디워핑 JPEG) base64. */
  image?: string;
  /** 담당 노드의 실시간 판정 이미지(<cctv>_parking.jpg) base64 — ROI·비율이 그려져 있다. 못 받으면 없음. */
  liveImage?: string;
  liveNode?: string;
  /** 판정 이미지 파일 시각(epoch 초). */
  liveAt?: number;
}

/** CCTV 한 대의 통로등(aisle)·만공차등(parking) — 종류별 켜진 수·전체 수·가장 밝은 값(0~100). */
export interface QaCctvLights {
  aisleOn: number;
  aisleTotal: number;
  aisleValue: number;
  parkingOn: number;
  parkingTotal: number;
  parkingValue: number;
  nodes: { kind: 'aisle' | 'parking'; modId: number; value: number }[];
}
export type QaDetection = NonNullable<QaCctvDetail['detection']>;
/** 자동 갱신 한 번 — 주차면 [id, status], QA 고정 CCTV, 이 층 카메라 판정·조명. */
export interface QaLive {
  spaces: [number, ParkingQaStatus][];
  held: string[];
  detection: Record<string, QaDetection>;
  lights: Record<string, QaCctvLights>;
  lightsError?: string;
  /** 이 층 통로등·만공차등 제어 모드. 수동이면 lights 값은 모드대로 채워져 온다. 못 읽었으면 없음. */
  lightMode?: { aisle: QaLightMode; parking: QaLightMode };
  now: number;
}

/** CCTV 한 대의 핑 결과 — 통합서버에서 2번 핑. ip 가 비었으면 핑하지 않았다. */
export interface QaPingCctv {
  name: string;
  level: string;
  ip: string;
  alive: boolean;
}

/** 모듈 통신 판정 — 무응답은 기간 내내 한 번도 응답 안 한 것, 응답률 95% 미만이면 불안정. */
export type QaModuleVerdict = 'ok' | 'unstable' | 'dead' | 'no_cctv' | 'no_modules' | 'no_data';
export type QaModuleWindow = '1h' | '24h' | '7d';

/** 모듈 하나(루트 127·BLE 128·통로등 40~)의 기간 통신 기록 — 조명서버가 5분마다 ping 한 값. */
export interface QaModuleHealth {
  modId: number;
  kind: 'root' | 'ble' | 'aisle';
  verdict: QaModuleVerdict;
  /** 지금 값: 1 응답 · 0 무응답 · -1 CCTV 연결 없음. */
  now?: number;
  /** 기간 중 응답한 시간 비율(0~1). */
  okRatio: number;
  /** 응답하다 끊긴 횟수. */
  drops: number;
  /** 기간 중 기록이 있는 분. */
  coverMinutes: number;
  /** 마지막으로 응답한 시각(유닉스 초). */
  lastOkAt?: number;
}

export interface QaCctvModules {
  name: string;
  ip: string;
  level: string;
  /** 가장 나쁜 모듈의 판정. 모듈이 없으면 no_modules. */
  verdict: QaModuleVerdict;
  modules: QaModuleHealth[];
}

export interface QaModuleReport {
  window: QaModuleWindow;
  windowMinutes: number;
  /** 통로등은 이 시간(분) 내내 응답이 없어야 무응답 — 사람이 없으면 한동안 조용할 수 있다. */
  aisleSilentMinutes?: number;
  /** 조명서버가 마지막으로 점검을 끝낸 시각(유닉스 초). 0 이면 점검 기능 없음. */
  scanAt: number;
  now: number;
  counts: Partial<Record<QaModuleVerdict, number>>;
  /** 나쁜 판정이 앞. */
  cctvs: QaCctvModules[];
}

export type QaBellIssue = 'no_id' | 'dup_id' | 'no_cctv' | 'missing_cctv' | 'not_nearest';

/** 문제가 있는 무선 비상벨 하나. not_nearest 면 가장 가까운 CCTV 와 두 거리(맵 좌표)를 같이 준다. */
export interface QaBellCheck {
  id: number;
  name: string;
  section: string;
  customId: string;
  cctv: string;
  issues: QaBellIssue[];
  nearest?: string;
  closer?: number;
  linkedDist?: number;
  nearestDist?: number;
}

export interface QaPairingReport {
  bellTotal: number;
  /** 문제 있는 벨만. */
  bells: QaBellCheck[];
  cctvTotal: number;
  aisleTotal: number;
  /** 통로등이 하나도 등록되지 않은 CCTV. */
  noAisle: string[];
  /** 어느 CCTV 에도 안 붙은 통로등 모듈. */
  unlinked: { id: number; modId: number; mac: string }[];
  /** 같은 통로등(MAC)이 여러 CCTV 에 등록된 것. */
  dupMac: { mac: string; cctvs: string[] }[];
}

/** 층 조명 한 종류의 제어 상태 — 수동이면 state 가 on/off. 조명 서비스에 없는 종류는 mode 가 빈 값. */
export interface QaLightMode {
  mode: 'auto' | 'manual' | '';
  state: 'on' | 'off' | '';
}
/** 조명 위치(P1_B1) 하나의 통로등·만공차등 모드와 통로등 밝기(%) — 평소 dimMin, 감지 시 dimMax. */
export interface QaLightControl {
  location: string;
  aisle: QaLightMode;
  parking: QaLightMode;
  /** 층 밝기가 등록되지 않았으면 없음. */
  dimMin?: number;
  dimMax?: number;
}
/** 바꿀 것만 담는다 — 모드는 종류별, 밝기는 dimMin·dimMax 둘 다. 비우면 읽기만. */
export interface QaLightChange {
  aisle?: QaLightMode;
  parking?: QaLightMode;
  dimMin?: number;
  dimMax?: number;
}

const path = (site: number) => `/v0.1/site-management/projects/${site}/parking-qa`;
const ipParams = (ip?: string) => (ip ? { params: { ip } } : undefined);

/** 실패하면 백엔드가 담은 사유(SSH 인증 실패, router 연결 실패 등)를 그대로 올린다. */
async function call<T>(req: () => Promise<T>): Promise<T> {
  try {
    return await req();
  } catch (e) {
    const message = axios.isAxiosError(e) ? e.response?.data?.message : undefined;
    throw typeof message === 'string' && message ? new Error(message) : e;
  }
}

/**
 * 현장 통합서버 router 의 QA 주차면 API. manage-backend 가 화면에서 입력한 통합서버 IP(ip),
 * 없으면 현장 네트워크 장비의 integration-server IP 로 프록시한다.
 */
export class SiteParkingQaService {
  /** 통합서버에 SSH(luxrobo 고정 계정)로 붙어 층별 맵을 읽는다. */
  static async map(site: number, ip: string): Promise<QaMapLevel[]> {
    const data = unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<{ levels: QaMapLevel[] }>>(
          `/v0.1/site-management/projects/${site}/qa-server/map`,
          { ip },
        ),
      ),
      '통합서버 맵 조회 실패',
    );
    return data.levels;
  }
  /** 주차면 DB 점유·종류와 단면 유도 전광판 구성 (여유/혼잡/만차 시나리오용). */
  static async state(site: number, ip: string): Promise<QaState> {
    return unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<QaState>>(
          `/v0.1/site-management/projects/${site}/qa-server/state`,
          { ip },
        ),
      ),
      '통합서버 주차 상태 조회 실패',
    );
  }
  /** CCTV 한 대의 연결 주차면·ROI·카메라 판정·배경 이미지. */
  static async cctvDetail(site: number, ip: string, name: string): Promise<QaCctvDetail> {
    return unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<QaCctvDetail>>(
          `/v0.1/site-management/projects/${site}/qa-server/cctv`,
          { ip, name },
        ),
      ),
      'CCTV 정보 조회 실패',
    );
  }
  /** 자동 갱신 — level 은 층 이름(B5), location 은 조명 위치(P1_B5). */
  static async live(site: number, ip: string, level: string, location: string): Promise<QaLive> {
    return unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<QaLive>>(
          `/v0.1/site-management/projects/${site}/qa-server/live`,
          { ip, level, location },
        ),
      ),
      '실시간 상태 조회 실패',
    );
  }
  /** 현장 DB 의 주차장 CCTV 전부를 통합서버에서 핑한다. */
  static async ping(site: number, ip: string): Promise<QaPingCctv[]> {
    const data = unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<{ cctvs: QaPingCctv[] }>>(
          `/v0.1/site-management/projects/${site}/qa-server/ping`,
          { ip },
        ),
      ),
      'CCTV 핑 점검 실패',
    );
    return data.cctvs;
  }
  /** CCTV 별 모듈(127·128·통로등) 통신 기록 — 현장 Prometheus 에서 기간(window) 단위로 집계. */
  static async modules(site: number, ip: string, window: QaModuleWindow): Promise<QaModuleReport> {
    return unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<QaModuleReport>>(
          `/v0.1/site-management/projects/${site}/qa-server/modules`,
          { ip, window },
        ),
      ),
      '모듈 통신 점검 실패',
    );
  }
  /** 무선 비상벨·통로등 페어링(현장 DB 등록) 점검. */
  static async pairing(site: number, ip: string): Promise<QaPairingReport> {
    return unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<QaPairingReport>>(
          `/v0.1/site-management/projects/${site}/qa-server/pairing`,
          { ip },
        ),
      ),
      '페어링 점검 실패',
    );
  }
  /** 조명 위치의 통로등·만공차등 자동/수동과 통로등 밝기 — change 만 바꾸고 바뀐 뒤 값을 돌려준다. */
  static async lightControl(
    site: number,
    ip: string,
    location: string,
    change: QaLightChange = {},
  ): Promise<QaLightControl> {
    return unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<QaLightControl>>(
          `/v0.1/site-management/projects/${site}/qa-server/lights-control`,
          { ip, location, ...change },
        ),
      ),
      '조명 제어 실패',
    );
  }
  static async cctvs(site: number, ip?: string): Promise<ParkingQaCctv[]> {
    const data = unwrapItem(
      await call(() =>
        apiClient.get<ApiItemResponse<{ cctvs: ParkingQaCctv[] }>>(
          `${path(site)}/cctvs`,
          ipParams(ip),
        ),
      ),
      'CCTV·주차면 조회 실패',
    );
    return data.cctvs;
  }
  static async setStatus(
    site: number,
    ids: number[],
    status: ParkingQaStatus,
    holdSeconds: number,
    ip?: string,
  ): Promise<ParkingQaVerdict[]> {
    const data = unwrapItem(
      await call(() =>
        apiClient.put<ApiItemResponse<{ verdicts: ParkingQaVerdict[] }>>(
          `${path(site)}/status`,
          { ids, status, holdSeconds },
          ipParams(ip),
        ),
      ),
      '주차면 상태 변경 실패',
    );
    return data.verdicts;
  }
  /** 주차면 상태를 유지하며 전체 CCTV 반영을 중지한다. */
  static async hold(site: number, holdSeconds: number, ip?: string): Promise<string[]> {
    const data = unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<{ held: string[] }>>(
          `${path(site)}/hold`,
          { holdSeconds },
          ipParams(ip),
        ),
      ),
      '카메라 반영 중지 실패',
    );
    return data.held;
  }
  /** 모든 QA 고정을 풀어 카메라 반영을 되돌린다. 풀린 CCTV 이름을 돌려준다. */
  static async release(site: number, ip?: string): Promise<string[]> {
    const data = unwrapItem(
      await call(() =>
        apiClient.post<ApiItemResponse<{ released: string[] }>>(
          `${path(site)}/release`,
          undefined,
          ipParams(ip),
        ),
      ),
      'QA 고정 해제 실패',
    );
    return data.released;
  }
}
